import React from 'react';
import { Card, Image } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { colorsModeUI } from '../modeUI';
import './index.css';
import {
  SECONDARY_COLOR,
  ALARM_COLOR,
  ALARM_COLOR_INVERTED,
  POSITIVE_COLOR,
  POSITIVE_COLOR_INVERTED,
  BLACK_COLOR,
  WARNING_COLOR,
  WHITE_COLOR,
  DISABLE_COLOR,
} from '../colors';

import {
  TYPE_POSITIVE,
  TYPE_NEGATIVE,
  TYPE_INFORMATION,
  TYPE_WARNING,
  TYPE_DISABLED,
  SIZE_SMALL,
} from '../constants';

/**
 * BoxPictureData
 * Show a box with buttons at the top, a picture in the middle and title at the bottom
 * Parameters (Props):
 * @param string title
 * @param string description
 * @param string picture
 * @param string type
 * @param string mode
 * @param func onClick
 * @param array icons
 */
export default class BoxPictureData extends React.PureComponent {
  constructor(props) {
    super(props);
    this.onClick = this.onClick.bind(this);
    this.state = {
      clickable: !!props.onClick,
    };
  }

  /**
   * OnClick
   * @param object event
   * @param object data
   */
  onClick(event, data) {
    const { onClick } = this.props;
    if (onClick) {
      onClick(event, data);
    }
  }

  render() {
    const { clickable } = this.state;
    const { title, description, picture, mode, icons, size } = this.props;

    let { type } = this.props;
    if (
      type !== TYPE_POSITIVE &&
      type !== TYPE_NEGATIVE &&
      type !== TYPE_WARNING &&
      type !== TYPE_DISABLED
    ) {
      type = TYPE_INFORMATION;
    }

    /* default colors used when the mode UI is not defined */
    let colors = {
      [TYPE_DISABLED]: {
        headerBackgroundColor: SECONDARY_COLOR,
        headerColor: BLACK_COLOR,
        backgroundColor: WHITE_COLOR,
        color: DISABLE_COLOR,
      },
      [TYPE_INFORMATION]: {
        headerBackgroundColor: SECONDARY_COLOR,
        headerColor: BLACK_COLOR,
        backgroundColor: WHITE_COLOR,
        color: BLACK_COLOR,
      },
      [TYPE_POSITIVE]: {
        headerBackgroundColor: POSITIVE_COLOR,
        headerColor: POSITIVE_COLOR_INVERTED,
        backgroundColor: WHITE_COLOR,
        color: BLACK_COLOR,
      },
      [TYPE_NEGATIVE]: {
        headerBackgroundColor: ALARM_COLOR,
        headerColor: ALARM_COLOR_INVERTED,
        backgroundColor: WHITE_COLOR,
        color: ALARM_COLOR,
      },
      [TYPE_WARNING]: {
        headerBackgroundColor: WARNING_COLOR,
        headerColor: SECONDARY_COLOR,
        backgroundColor: WHITE_COLOR,
        color: WARNING_COLOR,
      },
    };

    /* mode UI: dark mode/light mode */
    if (mode !== null) {
      const modeColors = colorsModeUI(mode);
      colors = {
        [TYPE_DISABLED]: {
          headerBackgroundColor:
            modeColors.BOX_HEADER_BACKGROUND_DISABLED_COLOR,
          headerColor: modeColors.BOX_HEADER_COLOR,
          backgroundColor: modeColors.BOX_BACKGROUND_COLOR,
          color: modeColors.DISABLE_COLOR,
          boxShadow: `0 1px 3px 0 ${modeColors.BOX_BORDER_COLOR}, 0 0 0 1px ${modeColors.BOX_BORDER_COLOR}`,
        },
        [TYPE_INFORMATION]: {
          headerBackgroundColor: modeColors.BOX_HEADER_BACKGROUND_COLOR,
          headerColor: modeColors.BOX_HEADER_COLOR,
          backgroundColor: modeColors.BOX_BACKGROUND_COLOR,
          color: modeColors.INFORMATION_COLOR,
          boxShadow: `0 1px 3px 0 ${modeColors.BOX_BORDER_COLOR}, 0 0 0 1px ${modeColors.BOX_BORDER_COLOR}`,
        },
        [TYPE_POSITIVE]: {
          headerBackgroundColor: modeColors.BOX_HEADER_BACKGROUND_COLOR,
          headerColor: modeColors.BOX_HEADER_COLOR,
          backgroundColor: modeColors.BOX_BACKGROUND_COLOR,
          color: modeColors.POSITIVE_COLOR,
          boxShadow: `0 1px 3px 0 ${modeColors.BOX_BORDER_COLOR}, 0 0 0 1px ${modeColors.BOX_BORDER_COLOR}`,
        },
        [TYPE_NEGATIVE]: {
          headerBackgroundColor: modeColors.BOX_HEADER_BACKGROUND_COLOR,
          headerColor: modeColors.BOX_HEADER_COLOR,
          backgroundColor: modeColors.BOX_BACKGROUND_COLOR,
          color: modeColors.ALARM_COLOR,
          boxShadow: `0 1px 3px 0 ${modeColors.BOX_BORDER_COLOR}, 0 0 0 1px ${modeColors.BOX_BORDER_COLOR}`,
        },
        [TYPE_WARNING]: {
          headerBackgroundColor: modeColors.BOX_HEADER_BACKGROUND_COLOR,
          headerColor: modeColors.BOX_HEADER_COLOR,
          backgroundColor: modeColors.BOX_BACKGROUND_COLOR,
          color: modeColors.WARNING_COLOR,
          boxShadow: `0 1px 3px 0 ${modeColors.BOX_BORDER_COLOR}, 0 0 0 1px ${modeColors.BOX_BORDER_COLOR}`,
        },
      };
    }
    const cardProps = {};
    if (clickable && type !== TYPE_DISABLED) {
      cardProps.onClick = this.onClick;
    }
    const styleButtons = {
      backgroundColor: colors[type].headerBackgroundColor,
      border: 'none',
      color: colors[type].headerColor,
    };
    const styleContent = {
      backgroundColor: colors[type].backgroundColor,
    };

    const styleTitle = {
      color: colors[type].color,
      textAlign: 'center',
    };

    const styleDescription = {
      color: colors[type].color,
    };

    return (
      <Card
        className={`box-picture-data ${size === SIZE_SMALL ? 'small' : ''}`}
        {...cardProps}
        style={{ boxShadow: colors[type].boxShadow }}
        centered
      >
        {icons && icons.length > 0 && (
          <Card.Content
            id="header"
            textAlign="left"
            style={styleButtons}
            className="title-buttons"
          >
            {icons.map((icon) => icon.button)}
          </Card.Content>
        )}
        <Image src={picture} size="medium" />
        <Card.Content style={styleContent}>
          <Card.Header id="title" style={styleTitle}>
            {title}
          </Card.Header>
          <Card.Description style={styleDescription}>
            {description}
          </Card.Description>
        </Card.Content>
      </Card>
    );
  }
}

BoxPictureData.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  picture: PropTypes.string.isRequired,
  type: PropTypes.string,
  onClick: PropTypes.func,
  mode: PropTypes.string,
  icons: PropTypes.arrayOf(Object),
  size: PropTypes.string,
};

BoxPictureData.defaultProps = {
  description: null,
  onClick: null,
  mode: null,
  icons: null,
  size: SIZE_SMALL,
};
